import { DateTime } from "luxon"
import { CalendarApi, EventApi, EventDropArg, EventMountArg } from "@fullcalendar/core"
import { EventResizeDoneArg } from "@fullcalendar/interaction"

import { DateTimeFactory } from "../../../../../../modules/localization/useDateTime"
import { tailwindPalette } from "../../../../../../modules/theme/tailwindPalette"
import { ResourceScheduleItem } from "../../../createOrderTypes"

import { TimelineCalendarEvent, TimelineCalendarResource } from "./fullCalendarTypes"

export const BUFFER_SIZE_ATTR = "--buffer-size"
export const SLOT_SIZE = 26
export const SLOT_DURATION = 15

const FREE_SLOT_EVENT_COLOR = tailwindPalette.blue[300]

export function isShortDayGridEvent(
    dateTimeFactory: DateTimeFactory,
    isDayGrid: boolean,
    start: Date | null | undefined,
    end: Date | null | undefined,
): boolean {
    if (!isDayGrid) return false
    if (!start || !end) return false

    const startDt = dateTimeFactory.fromJSDate(start)
    const endDt = dateTimeFactory.fromJSDate(end)
    const minutes = endDt.diff(startDt, "minutes").minutes

    return Number.isFinite(minutes) && minutes > 0 && minutes < SLOT_DURATION
}

export function toFloatingIsoMinutesFromJsDate(dateTimeFactory: DateTimeFactory, date: Date): string {
    return dateTimeFactory.toApiISOMinutesFormat(dateTimeFactory.fromJSDate(date))
}

export function isAllDayAvailabilitySlot(dateTimeFactory: DateTimeFactory, start: DateTime, end: DateTime): boolean {
    if (!start.isValid || !end.isValid) return false
    if (!(end > start)) return false
    if (dateTimeFactory.toHourMinute(start) !== "00:00" || dateTimeFactory.toHourMinute(end) !== "00:00") return false

    const hours = end.diff(start, "hours").hours
    return hours >= 23 && hours <= 25
}

export function isSlotFullyInPast(
    dateTimeFactory: DateTimeFactory,
    end: Date | null | undefined,
    now: DateTime,
): boolean {
    if (!end) return false
    if (!now.isValid) return false
    return dateTimeFactory.fromJSDate(end).toMillis() <= now.toMillis()
}

export function generateCalendarEvents(
    resources: ResourceScheduleItem[],
    intervalMinutes: number,
    dateTimeFactory: DateTimeFactory,
    resourceUidToCalendarResourceId: ReadonlyMap<string, string>,
): TimelineCalendarEvent[] {
    const events: TimelineCalendarEvent[] = []

    for (const { resource, availabilities: availability } of resources) {
        const calendarResourceId = resourceUidToCalendarResourceId.get(resource.uid) ?? resource.uid
        for (const entry of availability) {
            let slotStart = dateTimeFactory.fromISO(entry.startTime)
            let intervalEnd = dateTimeFactory.fromISO(entry.endTime)

            let isCurrentSlotFree = entry.availabilityView[0] === "0"
            let currentFreeSlotStart: DateTime | null = isCurrentSlotFree ? slotStart : null

            for (let i = 1; i <= entry.availabilityView.length; i++) {
                const isNextSlotFree = entry.availabilityView[i] === "0"
                const nextSlotStart = slotStart.plus({ minutes: intervalMinutes })

                if (!isNextSlotFree && isCurrentSlotFree) {
                    events.push({
                        id: `free-${resource.uid}-${currentFreeSlotStart!.toMillis()}-${i}`,
                        resourceIds: [calendarResourceId],
                        start: dateTimeFactory.toApiISOMinutesFormat(currentFreeSlotStart!),
                        end: dateTimeFactory.toApiISOMinutesFormat(nextSlotStart),
                        backgroundColor: FREE_SLOT_EVENT_COLOR,
                    })
                    currentFreeSlotStart = null
                }

                if (isNextSlotFree && !isCurrentSlotFree) {
                    currentFreeSlotStart = nextSlotStart
                }

                if (i === entry.availabilityView.length && currentFreeSlotStart) {
                    events.push({
                        id: `free-${resource.uid}-${currentFreeSlotStart.toMillis()}`,
                        resourceIds: [calendarResourceId],
                        start: dateTimeFactory.toApiISOMinutesFormat(currentFreeSlotStart),
                        end: dateTimeFactory.toApiISOMinutesFormat(intervalEnd),
                        subject: "Available",
                        canEdit: false,
                        outOfService: false,
                        backgroundColor: FREE_SLOT_EVENT_COLOR,
                    })
                }

                isCurrentSlotFree = isNextSlotFree
                slotStart = nextSlotStart
            }
        }
    }

    return events
}

export const getRoundedScrollTime = (dateTimeFactory: DateTimeFactory) => {
    const now = DateTime.local()
    const minusOneHour = now.minus({ hours: 1 })

    const boundedTime = minusOneHour < now.startOf("day") ? now.startOf("day") : minusOneHour

    const roundedMinutes = Math.floor(boundedTime.minute / 15) * 15

    const adjustedTime = boundedTime.set({
        minute: roundedMinutes,
        second: 0,
    })

    return dateTimeFactory.toHourMinute(adjustedTime)
}

export const isInPast = (iso: string | undefined | null): boolean => {
    if (!iso) return false
    return DateTime.fromISO(iso).diffNow().as("seconds") < 0
}

export function forceFullRevert(info: EventDropArg | EventResizeDoneArg) {
    const { oldEvent, view } = info

    info.revert()
    view.calendar.addEvent({ ...oldEvent })
}

function toRootFontSizePx(): number {
    const fontSize = globalThis.getComputedStyle?.(document.documentElement)?.fontSize
    const parsed = fontSize ? Number.parseFloat(fontSize) : Number.NaN
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 16
}

export const applyEventBufferStyle = (info: EventMountArg, resources: TimelineCalendarResource[]) => {
    const currentResourceId = info.el.closest("[data-resource-id]")?.getAttribute("data-resource-id")
    const resource = resources.find((res) => res.id === currentResourceId)
    const bufferForResource = resource?.buffer || 0

    if (bufferForResource <= 0) {
        info.el.style.setProperty(BUFFER_SIZE_ATTR, "0rem")
        return
    }

    // Compute buffer width from actual rendered event width (px) and event duration (minutes).
    // This matches how FullCalendar positions events (timeline rounds coords to whole pixels).
    const eventStart = info.event.start
    const eventEnd = info.event.end

    if (!eventStart || !eventEnd) return

    const start = DateTime.fromJSDate(eventStart)
    const end = DateTime.fromJSDate(eventEnd)
    const durationMinutes = end.diff(start, "minutes").minutes

    const eventWidthPx = info.el.getBoundingClientRect().width
    if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) return
    if (!Number.isFinite(eventWidthPx) || eventWidthPx <= 0) return

    const pxPerMinute = eventWidthPx / durationMinutes
    const bufferWidthPx = Math.round(bufferForResource * pxPerMinute)
    const bufferWidthRem = bufferWidthPx / toRootFontSizePx()

    info.el.style.setProperty(BUFFER_SIZE_ATTR, `${bufferWidthRem}rem`)
}

type HasDateRange = Pick<EventApi, "start" | "end"> | Pick<TimelineCalendarEvent, "start" | "end">
const toDateTime = (value: string | Date, dateTimeFactory: DateTimeFactory) =>
    typeof value === "string" ? dateTimeFactory.fromISO(value) : dateTimeFactory.fromJSDate(value)

export const isOngoing = (event: HasDateRange, dateTimeFactory: DateTimeFactory): boolean => {
    if (!event.start || !event.end) return false

    const now = dateTimeFactory.now()
    const start = toDateTime(event.start, dateTimeFactory)
    const end = toDateTime(event.end, dateTimeFactory)

    return start.isValid && end.isValid && start <= now && now < end
}

export const isSameResources = (a: readonly string[], b: readonly string[]) => {
    const setA = new Set(a)
    return b.every((id) => setA.has(id))
}

export async function scrollToResource(resourceId?: string): Promise<void> {
    if (!resourceId) return

    const scroller = document.querySelector<HTMLElement>(".fc-scroller.fc-scroller-liquid-absolute")
    if (!scroller) return

    const selector = `[data-resource-id="${resourceId}"]`

    const tryScroll = () => {
        const target = document.querySelector<HTMLElement>(selector)
        if (!target) return false

        const scrollTop = target.offsetTop - scroller.clientHeight / 2.5 + target.clientHeight

        let attempts = 0
        const maxAttempts = 7

        const animateScroll = () => {
            scroller.scrollTo({ top: scrollTop, behavior: "smooth" })
            attempts++
            if (attempts < maxAttempts) {
                requestAnimationFrame(animateScroll)
            }
        }

        requestAnimationFrame(animateScroll)
        return true
    }

    return new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
            if (tryScroll()) {
                setTimeout(resolve, 300)
                return
            }

            const observer = new MutationObserver(() => {
                if (tryScroll()) {
                    observer.disconnect()
                    setTimeout(resolve, 300)
                }
            })

            observer.observe(scroller, { childList: true, subtree: true })

            setTimeout(() => {
                observer.disconnect()
                resolve()
            }, 3000)
        })
    })
}

export function applyDynamicTimeBuffer(calendarApi: CalendarApi, start: DateTime) {
    calendarApi.setOption("slotMinTime", start.toFormat("HH:mm:ss"))
    calendarApi.setOption("slotMaxTime", `${start.hour + 24}:00:00`)
    calendarApi.setOption("duration", { days: 2 })
}
